import { SkyDialog, SkyText } from "@skypointcloud/skypoint-ui";
import type { McpGrantView } from "@shared/mcp/consent";
import { InlineErrorBar, type InlineFailure } from "@/lib/feedback/InlineErrorBar";

/**
 * Disconnecting one AI app (ADR 0009 section 4). The title names the connection ("Claude, Sales
 * production"), so a screen reader hears what ends. It ends at once: the app's tokens are deleted
 * and its next call is refused; the app must be connected again, with a new approval, to read
 * anything. The dialog stays open until the revoke lands, so a refusal is said inside it.
 */
export function RevokeMcpGrantDialog({
  grant,
  isSubmitting,
  error,
  onConfirm,
  onCancel,
  whose,
}: {
  grant: McpGrantView | null;
  isSubmitting: boolean;
  error: InlineFailure | null;
  onConfirm: (grant: McpGrantView) => void;
  onCancel: () => void;
  /** "your" on Connected apps; the person's name on an admin list. */
  whose: string;
}) {
  return (
    <SkyDialog
      open={grant !== null}
      onOpenChange={(open) => {
        if (!open) onCancel();
      }}
      onBeforeClose={(reason) => reason !== "primaryAction"}
      size="medium"
      testId="mcp-revoke-dialog"
      title={grant ? `Disconnect ${grant.name}?` : "Disconnect this app?"}
      description={`${grant?.clientName ?? "The app"} stops reading ${whose} skyData at once.`}
      primaryAction={{
        label: "Disconnect",
        destructive: true,
        loading: isSubmitting,
        onClick: () => {
          if (grant) onConfirm(grant);
        },
      }}
      secondaryAction={{ label: "Keep it connected", onClick: onCancel }}
    >
      <div className="flex flex-col gap-3">
        <SkyText as="p" size={300} block>
          Its sign-in is ended and cannot be used again. To use it later, connect skyData again from
          the app and approve it here.
        </SkyText>
        <InlineErrorBar
          error={error}
          title="The app was not disconnected"
          testId="mcp-revoke-error"
        />
      </div>
    </SkyDialog>
  );
}
